import React from "react";
import cookie from "react-cookies";
import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, makeStyles, Typography } from "@material-ui/core";
import { compose } from "redux";
import { connect } from "react-redux";
import { CustomView, isIOS } from "react-device-detect";
import { Field, formValueSelector, reduxForm } from "redux-form";
import { FormattedMessage, IntlProvider } from "react-intl";

import * as actions from "../../../actions";
import customInput, { CustomDateOfBirthPicker } from "../../../custom/CustomInput";
import customSelect from "../../../custom/CustomSelect";
import messages_br from "../../../translations/br.json";
import messages_en from "../../../translations/en.json";
import messages_es from "../../../translations/es.json";
import { asyncValidateRegister as asyncValidate, numberNormalizer, capitalCaseNormalizer, resolveListErrors, USER_LOCALES, PERSON_TYPES, PERSON_TYPE } from "../../../util";
import { NORMALIZER_BY_DOCUMENT_TYPE, TAX_DOCUMENT_TYPES, getTaxDocumentTypes } from "../../../util/taxDocuments";
import {
  validateConfirmEmail,
  validateDateOfBirth,
  validateDocumentId,
  validateDocumentType,
  validateEmail,
  validateLocale,
  validateName,
  validatePassword,
  validateTaxResidence,
  validateType,
} from "../../../util/validations";

import { country } from "../Terms";
import PasswordReduxField from "../PasswordReduxField";
import TermsAndConditions from "./TermsAndConditions";
import OnboardingLinks from "./OnboardingLinks";

const FormCheckbox = ({ input, label, ...props }) => {
  return <Checkbox label={label} checked={input.value ? true : false} onChange={input.onChange} {...props} />;
};

const messages = {
  br: messages_br,
  en: messages_en,
  es: messages_es,
};

const validateTerms = (value) => (value ? undefined : "app.termsAndConditions");

const OnboardingForm = ({
  documentType,
  invite,
  taxResidence,
  type,
  locale: currentLocale,

  pristine,
  submitting,
  invalid,

  change,
  handleSubmit,

  history,

  showSuccessMessage,
  showErrorMessage,
  ...props
}) => {
  const classes = useStyles();

  const [locale, setLocale] = React.useState(currentLocale);
  const [hasReachedBottom, setHasReachedBottom] = React.useState(false);
  const [userData, setUserData] = React.useState(null);
  const [openTerms, setOpenTerms] = React.useState(false);

  const [loading, setLoading] = React.useState(false);

  const onSubmit = (formProps) => {
    setUserData(formProps);
    setOpenTerms(true);
  };

  const accept = async () => {
    const params = {
      ...userData,
      inviteToken: invite?.token,
      email: userData?.email?.trim() ?? "",
      confirmEmail: userData?.confirmEmail?.trim() ?? "",

      name: capitalCaseNormalizer(userData?.name?.trim()) ?? "",
      documentId: userData?.documentId?.trim() ?? "",
    };

    try {
      setLoading(true);
      await actions.registerUser(params);
      const expires = new Date();
      expires.setFullYear(expires.getFullYear() + 1);
      cookie.save("lclocale", locale, { path: "/", expires });
      showSuccessMessage("app.register.success");
      history.push("/");
    } catch (e) {
      const errors = resolveListErrors(e);
      const firstError = errors[0];
      showErrorMessage(firstError.message);
      setOpenTerms(false);
    }
    setLoading(false);
  };

  const onChangeType = (type) => {
    change("personType", type);
    change("documentType", null);
    change("documentId", null);
  };

  const onChangeTaxResidence = (taxResidence) => {
    change("taxResidence", taxResidence);
    change("documentType", null);
    change("documentId", null);
  };

  const onChangeDocumentType = (documentType) => {
    change("documentType", documentType);
    change("documentId", null);
  };

  const onChangeLanguageTerm = (locale) => {
    setLocale(locale);
    change("locale", locale);
  };

  const isPersonNatural = type === PERSON_TYPE.NATURAL_PERSON;

  const documents = taxResidence ? getTaxDocumentTypes(taxResidence, type) : [TAX_DOCUMENT_TYPES.SELECT];
  const documentNormalizer = NORMALIZER_BY_DOCUMENT_TYPE[documentType];

  return (
    <IntlProvider locale={locale} key={locale} messages={messages[locale]}>
      <div className={classes.root}>
        <Typography variant="h5" gutterBottom color="primary" className={classes.title}>
          Onboarding
        </Typography>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Field
            name="locale"
            label="app.language"
            component={customSelect}
            values={USER_LOCALES}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => onChangeLanguageTerm(e.target.value)}
            className={classes.field}
          />
          <Field
            name="type"
            label="app.personType"
            component={customSelect}
            values={PERSON_TYPES}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => onChangeType(e.target.value)}
            className={classes.field}
          />
          <Field name="name" label="app.name" component={customInput} InputLabelProps={{ shrink: true }} className={classes.field} />
          <Field name="taxResidence" label="app.taxResidence" component={customSelect} values={country} InputLabelProps={{ shrink: true }} className={classes.field} onChange={onChangeTaxResidence} />
          <Field
            name="documentType"
            label="app.documentType"
            component={customSelect}
            values={documents}
            InputLabelProps={{ shrink: true }}
            className={classes.field}
            onChange={onChangeDocumentType}
          />
          <Field name="documentId" label="app.documentIdTax" component={customInput} InputLabelProps={{ shrink: true }} normalize={documentNormalizer} className={classes.field} />
          {isPersonNatural && (
            <>
              <CustomView condition={isIOS}>
                <Field name="dateOfBirth" label="app.dateOfBirth" component={customInput} InputLabelProps={{ shrink: true }} type="date" className={classes.field} />
              </CustomView>
              <CustomView condition={!isIOS}>
                <Field name="dateOfBirth" label="app.dateOfBirth" component={CustomDateOfBirthPicker} InputLabelProps={{ shrink: true }} className={classes.field} />
              </CustomView>
            </>
          )}
          <Field name="email" label="app.email" component={customInput} InputLabelProps={{ shrink: true }} className={classes.field} />
          <Field name="confirmEmail" label="app.confirmEmail" component={customInput} InputLabelProps={{ shrink: true }} className={classes.field} />
          <PasswordReduxField name="password" label="app.password" shrink showStrength />
          <PasswordReduxField name="confirmPassword" label="app.confirmPassword" shrink />
          <Field name="agentId" label="app.agentId" component={customInput} InputLabelProps={{ shrink: true }} InputProps={{ disabled: true }} normalize={numberNormalizer} className={classes.field} />
          <Typography gutterBottom variant="caption" color="primary" className={classes.registerText}>
            <FormattedMessage id="app.register.text" />
          </Typography>
          <Button color="primary" variant="contained" type="submit" fullWidth className={classes.submitButton} disabled={pristine || invalid || submitting}>
            <FormattedMessage id="app.signup" />
          </Button>
        </form>

        <Dialog
          open={openTerms}
          variant="secondary"
          fullWidth
          maxWidth="md"
          onClose={() => {
            setOpenTerms(false);
            setHasReachedBottom(false);
          }}
        >
          <DialogTitle className={classes.dialogTitle}>
            <div className={classes.dialogTitleContent}>
              <FormattedMessage id="app.confirmTerms" />
            </div>
          </DialogTitle>

          <DialogContent>
            <div className={classes.termsContent}>
              <Typography align="center" className={classes.termsLink}>
                <FormattedMessage id="app.termsAndConditions.link" />
              </Typography>

              <TermsAndConditions locale={locale} hasReachedBottom={hasReachedBottom} onReachedBottom={() => setHasReachedBottom(true)} />

              <DialogActions className={classes.dialogActions}>
                <FormControlLabel
                  label={<FormattedMessage id="app.withdrawTerms" />}
                  control={<Field component={FormCheckbox} name="termsAndConditions" color="primary" validate={validateTerms} disabled={!hasReachedBottom} />}
                />

                <Button
                  type="button"
                  fullWidth
                  variant="contained"
                  color="primary"
                  className={classes.dialogSubmitButton}
                  disabled={pristine || invalid || submitting || !hasReachedBottom || loading}
                  onClick={accept}
                >
                  <FormattedMessage id="app.signup" />
                </Button>
              </DialogActions>
            </div>
          </DialogContent>
        </Dialog>

        <OnboardingLinks locale={locale} />
      </div>
    </IntlProvider>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },

  title: {
    textAlign: "center",
  },

  field: {
    width: "100%",
  },

  registerText: {
    marginTop: theme.spacing(1),
  },

  submitButton: {
    marginTop: theme.spacing(2),
  },

  dialogTitle: {
    color: "black",
    backgroundColor: theme.palette.primary.main,
  },

  dialogTitleContent: {
    textAlign: "center",
  },

  termsContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "justify",
    gap: theme.spacing(1.5),
  },

  termsLink: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    paddingTop: theme.spacing(1.5),
  },

  dialogActions: {
    flexDirection: "column",
    alignItems: "stretch",
  },

  dialogSubmitButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

const validate = (values, props) => {
  const errors = {};

  validateType({ values, errors });
  validateName({ values, errors });
  validateLocale({ values, errors });
  validateTaxResidence({ values, errors });
  validateDocumentType({ values, errors });
  validateDocumentId({ values, errors });
  validateDateOfBirth({ values, errors });
  validateEmail({ values, errors });
  validateConfirmEmail({ values, errors });
  validatePassword({ values, errors });
  // validateAgentId({values, errors});

  return errors;
};

const selector = formValueSelector("registerForm");

function mapStateToProps(state, props) {
  const invite = props.invite || {};
  const currentLocale = invite?.locale || "en";
  return {
    initialValues: {
      email: invite.email || "",
      name: invite.name || "",
      locale: currentLocale,

      agentId: invite.agentId || "",
      documentId: invite?.documentId || "",
      documentType: invite?.documentType || "",
      taxResidence: invite?.taxResidence || "",
      type: invite?.personType || "",
    },
    locale: currentLocale,
    auth: state.auth.authenticated,
    currentUser: state.user.user.me,
    taxResidence: selector(state, "taxResidence"),
    documentType: selector(state, "documentType"),
    type: selector(state, "type"),
  };
}

export default compose(
  connect(mapStateToProps, actions),
  reduxForm({
    form: "registerForm",
    enableReinitialize: false,
    keepDirtyOnReinitialize: false,
    asyncValidate,
    asyncBlurFields: ["email", "documentId", "taxResidence"],
    validate,
    touchOnChange: true,
  }),
)(OnboardingForm);
